import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { useAuthContext } from '../../common/hooks/use-auth-context.hook';
import AuthClientStore from '../../common/services/auth-client.service';
import { PresenceStatus, usePresenceStore } from './presence.store';

const API_URL = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';

/** No keyboard or pointer for this long and a tab counts as away. */
const IDLE_AFTER_MS = 3 * 60_000;
/** A hidden tab is away sooner — but not for a glance at another tab. */
const HIDDEN_AFTER_MS = 60_000;
const CHECK_EVERY_MS = 10_000;
/** A refused handshake is not retried by socket.io itself; this is. */
const RETRY_AFTER_MS = 15_000;

const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

const DEVICE_KEY = 'ardealy.bo.presence.device';

/**
 * This browser's own id, kept across reloads. Super admins have no seat
 * session, and one staff login is often shared: without this, two colleagues
 * on it would each be taken for "me" and hidden from the other. When storage
 * is unavailable the id lasts for the page load, which is still per-person.
 */
function browserId(): string {
  const fresh = () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  try {
    const stored = localStorage.getItem(DEVICE_KEY);
    if (stored && /^[A-Za-z0-9-]{8,64}$/.test(stored)) return stored;
    const id = fresh();
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return fresh();
  }
}

/**
 * Keeps this tab on the list of who is in the backoffice, and the list itself
 * current. Mounted once in App.tsx, inside the router, for the same reason as
 * the bell's provider: the top bar remounts on every navigation.
 *
 * Opens the API's /presence namespace. socket.io multiplexes it over the same
 * connection as /notifications, so it costs no second socket.
 *
 * The tab tells the server three things, and only when one changes: the path
 * it is on, what that page calls itself (its breadcrumbs, reported by the top
 * bar), and whether anyone is at the keyboard.
 */
export const PresenceProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthContext();
  const { pathname } = useLocation();
  const page = usePresenceStore((s) => s.page);

  const socketRef = useRef<Socket | null>(null);
  const statusRef = useRef<PresenceStatus>('active');
  const lastInputRef = useRef(Date.now());
  const hiddenAtRef = useRef<number | null>(null);
  const reportRef = useRef<{ path: string; crumbs: string[] }>({ path: pathname, crumbs: [] });

  const crumbs = page && page.path === pathname ? page.crumbs : null;

  /* What this tab would say about itself right now. */
  reportRef.current = { path: pathname, crumbs: crumbs ?? [] };

  const emitFull = () => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit('presence:update', {
      ...reportRef.current,
      status: statusRef.current,
      idleForMs: Date.now() - lastInputRef.current,
    });
  };

  // The socket: open while signed in.
  useEffect(() => {
    if (!isAuthenticated) {
      usePresenceStore.getState().reset();
      return;
    }

    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | null = null;

    const socket: Socket = io(`${API_URL}/presence`, {
      reconnection: true,
      reconnectionDelay: 2000,
      auth: { token: AuthClientStore.getAccessToken(), device: browserId() },
    });
    socketRef.current = socket;

    const freshToken = () => {
      (socket.auth as { token?: string }).token = AuthClientStore.getAccessToken();
    };
    // Access tokens rotate hourly; a long-lived tab must reconnect with the current one.
    const onReconnectAttempt = () => freshToken();
    socket.io.on('reconnect_attempt', onReconnectAttempt);

    const scheduleRetry = () => {
      if (cancelled || retry) return;
      retry = setTimeout(() => {
        retry = null;
        if (cancelled || socket.connected) return;
        freshToken();
        socket.connect();
      }, RETRY_AFTER_MS);
    };

    socket.on('connect', emitFull);
    socket.on('presence:hello', (me) => {
      if (!cancelled && me?.key) usePresenceStore.getState().setSelf(String(me.key), String(me.userId || ''));
    });
    socket.on('presence:state', (state) => {
      if (!cancelled) usePresenceStore.getState().setMembers(state?.members);
    });
    // Refused handshake (an expired token, most often): socket.io gives up on its own.
    socket.on('connect_error', () => {
      if (!socket.active) scheduleRetry();
    });
    socket.on('disconnect', (reason) => {
      // The list is stale the moment we are off it; show no one rather than ghosts.
      if (!cancelled) usePresenceStore.getState().setMembers([]);
      if (reason === 'io server disconnect') scheduleRetry();
    });

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
      socket.disconnect();
      socketRef.current = null;
      usePresenceStore.getState().reset();
    };
    // emitFull reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Active / away, from input and tab visibility.
  useEffect(() => {
    if (!isAuthenticated) return;

    const setStatus = (next: PresenceStatus) => {
      if (statusRef.current === next) return;
      statusRef.current = next;
      emitFull();
    };

    const evaluate = () => {
      const now = Date.now();
      const idle = now - lastInputRef.current > IDLE_AFTER_MS;
      const hidden = hiddenAtRef.current !== null && now - hiddenAtRef.current > HIDDEN_AFTER_MS;
      setStatus(idle || hidden ? 'away' : 'active');
    };

    let lastMark = 0;
    const onInput = () => {
      const now = Date.now();
      // pointermove fires constantly; the time only needs to be right to the second.
      if (now - lastMark < 1000 && statusRef.current === 'active') return;
      lastMark = now;
      lastInputRef.current = now;
      if (statusRef.current === 'away' && document.visibilityState === 'visible') setStatus('active');
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else {
        hiddenAtRef.current = null;
        // Coming back to the tab is a touch in itself.
        lastInputRef.current = Date.now();
        setStatus('active');
      }
    };

    hiddenAtRef.current = document.visibilityState === 'hidden' ? Date.now() : null;
    INPUT_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    const timer = setInterval(evaluate, CHECK_EVERY_MS);

    return () => {
      INPUT_EVENTS.forEach((e) => window.removeEventListener(e, onInput));
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Where this tab is. Debounced so a navigation and the new page's crumbs,
  // which arrive a render apart, go out as one report.
  const crumbsKey = (crumbs ?? []).join('\u0000');
  useEffect(() => {
    if (!isAuthenticated) return;
    const timer = setTimeout(() => {
      const socket = socketRef.current;
      if (socket?.connected) socket.emit('presence:update', reportRef.current);
    }, 250);
    return () => clearTimeout(timer);
  }, [isAuthenticated, pathname, crumbsKey]);

  return <>{children}</>;
};

/**
 * Called by the top bar with the page's breadcrumbs, so teammates see
 * "Catalog › Oak plank" rather than a path.
 */
export function useReportPresencePage(pathname: string, crumbs: string[]) {
  const key = crumbs.join('\u0000');
  useEffect(() => {
    usePresenceStore.getState().reportPage(pathname, crumbs);
    // `key` stands in for the array, which is new on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, key]);
}
